import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid, useTheme
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const StatsForm = ({ authHeaders, backEnd }) => {
  const theme = useTheme();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    city: "",
    location: "",
    period: 3,
  });

  const [stats, setStats] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  function groupCityAndRegion(data) {
  const grouped = [];
  const seen = new Set();

  const allKeys = Object.keys(data);

  allKeys.forEach(key => {
    if (seen.has(key)) return;

    if (!key.startsWith("област ")) {
      const regionKey = "област " + key;

      // If corresponding region exists, group both
      if (data[regionKey]) {
        grouped.push([key, data[key]]);
        grouped.push([regionKey, data[regionKey]]);
        seen.add(key);
        seen.add(regionKey);
      } else {
        // Just city
        grouped.push([key, data[key]]);
        seen.add(key);
      }
    }
  });

  // Now add any unmatched "област ..." entries
  allKeys.forEach(key => {
    if (key.startsWith("област ") && !seen.has(key)) {
      grouped.push([key, data[key]]);
      seen.add(key);
    }
  });

  return Object.fromEntries(grouped);
}

function groupCityAndRegionWithRenamedKeys(data) {
  const grouped = [];
  const seen = new Set();
  const allKeys = Object.keys(data);

  allKeys.forEach(key => {
    if (seen.has(key)) return;

    if (!key.startsWith("област ")) {
      const regionKey = "област " + key;

      if (data[regionKey]) {
        grouped.push([key, data[key]]);
        grouped.push([`${key} област`, data[regionKey]]);
        seen.add(key);
        seen.add(regionKey);
      } else {
        grouped.push([key, data[key]]);
        seen.add(key);
      }
    }
  });

  allKeys.forEach(key => {
    if (key.startsWith("област ") && !seen.has(key)) {
      const cityName = key.replace("област ", "");
      grouped.push([`${cityName} област`, data[key]]);
      seen.add(key);
    }
  });

  return Object.fromEntries(grouped);
}




  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(backEnd, {
          method: "GET",
          headers: { Authorization: authHeaders },
        });

        if (!response.ok) throw new Error("Failed to fetch data");

        const responseData = await response.json();
        // const groupedData = groupCityAndRegion(responseData);
        const final = groupCityAndRegionWithRenamedKeys(responseData)
        setData(final);
      } catch (err) {
        setError("Failed to load data.");
        setSnackbar({ open: true, message: err.message, severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authHeaders, backEnd]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "city" ? { location: "" } : {}), // Reset location when city changes
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(backEnd + "get_stats", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authHeaders,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch statistics");
      }

      const result = await response.json();
      setStats(result);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setSnackbar({ open: true, message: "Failed to retrieve statistics.", severity: "error" });
    }
  };

  return (
    <Box sx={{ maxWidth: 900, margin: "auto", mt: 4, textAlign: "left" }}>
      <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 'bold', textAlign: 'center'}}> 
        Заяви статистика
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {/* City Dropdown */}
          <TextField
            select
            label="Град"
            name="city"
            value={formData.city}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {Object.keys(data).map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </TextField>

          {/* Location Dropdown */}
          <TextField
            select
            label="Квартал"
            name="location"
            value={formData.location}
            onChange={handleChange}
            fullWidth
            margin="normal"
            disabled={!formData.city}
          >
            {formData.city && data[formData.city]?.length > 0 ? (
              data[formData.city].map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No locations available</MenuItem>
            )}
          </TextField>

          {/* Period Dropdown */}
          <TextField
            select
            label="Период (в месеци)"
            name="period"
            value={formData.period}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {[...Array(12)].map((_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {i + 1} Месец (и)
              </MenuItem>
            ))}
          </TextField>

          {/* Submit Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ marginTop: 2 }}
            disabled={!formData.city}
          >
            Генерирай статистики
          </Button>
        </>
      )}

      {/* Display Statistics */}
      {stats && (
        <Box sx={{ mt: 3, p: 2, border: "1px solid #ddd", borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="h6">Средна цена за града: {stats.city_avg}€</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6">Средна цена за квартала: {stats.location_avg}€</Typography>
              <Typography variant="h5"></Typography>
            </Grid>
          </Grid>

          {/* Prices and Stats Accordion */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Цени и средни стойности</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.keys(stats.extra).map((location) => (
                <Accordion key={location} sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      {location} - Средна цена за квартала: {stats.extra[location].avg_price}€
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h6">Средна цена за: (€):</Typography>
                        {Object.entries(stats.extra[location].room_price_avg).map(([rooms, price]) => (
                          <Typography key={rooms}>
                            {rooms}-стаен: {price}€
                          </Typography>
                        ))}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h6">Разпределение (%):</Typography>
                        {Object.entries(stats.extra[location].room_dist).map(([rooms, percent]) => (
                          <Typography key={rooms}>
                            {rooms}-стаен: {percent}%
                          </Typography>
                          
                        ))}
                      </Grid>

                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>

          {/* Charts Accordion */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Визуализация на цените</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.charts.city_avg}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="avg" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>

              {Object.keys(stats.charts.locations).map((location) => (
                <Accordion key={location} sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">{location}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.charts.locations[location].avg_price}>
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="avg" fill="#82ca9d" />
                      </BarChart>
                    </ResponsiveContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default StatsForm;
